import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/TopNav";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ReferralCard } from "@/components/profile/ReferralCard";
import { ReferralLeaderboard } from "@/components/profile/ReferralLeaderboard";
import { MonthlyDigestCard } from "@/components/profile/MonthlyDigestCard";
import { WeeklyDigestCard } from "@/components/profile/WeeklyDigestCard";
import { PasskeyCard } from "@/components/profile/PasskeyCard";
import { RiskProfileCard } from "@/components/profile/RiskProfileCard";
import { HalalModeCard } from "@/components/profile/HalalModeCard";
import { StatementsCard } from "@/components/profile/StatementsCard";
import { DeviceSessions } from "@/components/profile/DeviceSessions";
import { Mail, Phone, IdCard, Camera, CheckCircle2, ShieldCheck, Globe, Contrast, SlidersHorizontal, Bell, TrendingUp, CalendarClock, Send, Lock, KeyRound, MessageCircle, MapPin, Save as SaveIcon, Sun, Moon } from "lucide-react";

const COUNTRIES = [
  "Qatar", "Saudi Arabia", "United Arab Emirates", "Kuwait", "Bahrain", "Oman",
  "United States", "United Kingdom", "Eurozone", "Japan", "Switzerland",
  "Canada", "Australia", "India", "Pakistan", "Egypt", "Turkey", "Singapore", "China",
];
const COUNTRY_CCY = {
  "Qatar": "QAR", "Saudi Arabia": "SAR", "United Arab Emirates": "AED", "Kuwait": "KWD",
  "Bahrain": "BHD", "Oman": "OMR", "United States": "USD", "United Kingdom": "GBP",
  "Eurozone": "EUR", "Japan": "JPY", "Switzerland": "CHF", "Canada": "CAD",
  "Australia": "AUD", "India": "INR", "Pakistan": "PKR", "Egypt": "EGP",
  "Turkey": "TRY", "Singapore": "SGD", "China": "CNY",
};

const STEPS = [
  { key: "email", label: "Email", icon: Mail },
  { key: "phone", label: "Phone", icon: Phone },
  { key: "id", label: "ID Upload", icon: IdCard },
  { key: "selfie", label: "Selfie", icon: Camera },
];

const ALERT_OPTIONS = [
  { key: "compliance", label: "Shariah compliance alerts", desc: "When a watchlist stock changes status", icon: ShieldCheck },
  { key: "price", label: "Price alerts", desc: "Large moves on your watchlist", icon: TrendingUp },
  { key: "corporate_actions", label: "Corporate actions", desc: "Dividends, AGMs, bonus issues", icon: CalendarClock },
  { key: "email_delivery", label: "Email delivery", desc: "Also send alerts to your email", icon: Send },
];

const statusColor = { approved: "#5C7020", pending_review: "#6E8524", in_progress: "#1E5AA8", rejected: "#C2413A", not_started: "#4B5565" };

export default function Profile() {
  const { user, refreshUser, updateContact, lang, setLang, contrast, setContrast, t, theme, setTheme } = useApp();
  const [kyc, setKyc] = useState(null);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [alertPrefs, setAlertPrefs] = useState({ compliance: true, price: false, corporate_actions: false, email_delivery: false });
  const idRef = useRef(); const selfieRef = useRef();

  // Editable contact / personal details
  const [contact, setContact] = useState({ name: "", email: "", phone: "", whatsapp: "", address: "", country: "Qatar", base_currency: "QAR" });
  const [contactBusy, setContactBusy] = useState(false);

  // MFA / 2FA
  const [mfa, setMfa] = useState({ enrolled: false, enabled: false });
  const [mfaSetup, setMfaSetup] = useState(null); // { secret, otpauth_uri }
  const [mfaCode, setMfaCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/kyc"); setKyc(data);
    try { const p = await api.get("/notifications/preferences"); setAlertPrefs(p.data); } catch (e) { console.error("Failed to load notification preferences:", e); }
    try { const m = await api.get("/foundation/mfa/status"); setMfa(m.data); } catch (e) { console.error("Failed to load MFA status:", e); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!user) return;
    setContact({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      whatsapp: user.whatsapp || "",
      address: user.address || "",
      country: user.country || "Qatar",
      base_currency: user.base_currency || COUNTRY_CCY[user.country || "Qatar"] || "QAR",
    });
  }, [user]);

  const saveContact = async () => {
    setContactBusy(true);
    try {
      await updateContact(contact);
      toast.success("Details updated", { description: "Your contact information has been saved" });
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || "Please try again" });
    } finally { setContactBusy(false); }
  };

  const startEnroll = async () => {
    setMfaBusy(true);
    try { const { data } = await api.post("/foundation/mfa/enroll"); setMfaSetup(data); setMfaCode(""); }
    catch (e) { toast.error("Could not start 2FA setup"); }
    finally { setMfaBusy(false); }
  };
  const confirmEnable = async () => {
    if (mfaCode.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setMfaBusy(true);
    try {
      await api.post("/foundation/mfa/verify", { code: mfaCode });
      toast.success("Two-factor authentication enabled");
      setMfaSetup(null); setMfaCode("");
      await load(); await refreshUser();
    } catch (e) { toast.error("Invalid code", { description: "Check your authenticator and try again" }); }
    finally { setMfaBusy(false); }
  };
  const disableMfa = async () => {
    if (mfaCode.length !== 6) { toast.error("Enter your current 6-digit code to disable"); return; }
    setMfaBusy(true);
    try {
      await api.post("/foundation/mfa/disable", { code: mfaCode });
      toast.success("Two-factor authentication disabled");
      setMfaCode(""); await load(); await refreshUser();
    } catch (e) { toast.error("Invalid code"); }
    finally { setMfaBusy(false); }
  };

  const togglePro = async (value) => {
    try {
      await api.patch("/auth/preferences", { pro_mode: value });
      await refreshUser();
      toast.success(value ? "Pro mode on" : "Pro mode off", {
        description: value ? "The trading desk, orders and world markets are back in your menu."
                           : "Your menu is back to the simple essentials.",
      });
    } catch (e) {
      console.error("Couldn't switch pro mode:", e);
      toast.error("Couldn't switch that just now");
    }
  };

  const toggleAlert = async (key, value) => {
    setAlertPrefs((p) => ({ ...p, [key]: value }));
    try { const { data } = await api.patch("/notifications/preferences", { [key]: value }); setAlertPrefs(data); toast.success("Alert preferences updated"); }
    catch (e) { toast.error("Failed to update"); }
  };

  const fileToB64 = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

  const doStep = async (step, value) => {
    setBusy(true);
    try { const { data } = await api.post("/kyc/step", { step, value }); setKyc(data);
      toast.success(`${step} verified`); await refreshUser();
    } catch (e) { toast.error("Failed", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const upload = async (step, ref) => {
    const file = ref.current?.files?.[0];
    if (!file) return;
    const b64 = await fileToB64(file);
    await doStep(step, b64);
  };

  const steps = kyc?.steps || {};
  const done = STEPS.filter((s) => steps[s.key]).length;
  const progress = (done / STEPS.length) * 100;

  return (
    <AppShell>
      <PageHeader title="Profile & Settings" subtitle="Identity verification, security and preferences" testid="profile-header" />

      <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4 sm:space-y-5">
          <Card className="p-5 shadow-premium" data-testid="kyc-stepper">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">KYC Verification</div>
              <Badge style={{ background: `${statusColor[kyc?.status] || "#4B5565"}22`, color: statusColor[kyc?.status] || "#4B5565" }} className="capitalize">{(kyc?.status || "not started").replace("_", " ")}</Badge>
            </div>
            <Progress value={progress} className="mb-5" />
            <div className="grid sm:grid-cols-2 gap-3">
              {STEPS.map((s) => (
                <div key={s.key} className={`rounded-lg border p-4 ${steps[s.key] ? "border-emerald-200 bg-emerald-50/40" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><s.icon className="h-4 w-4" /><span className="font-medium text-sm">{s.label}</span></div>
                    {steps[s.key] && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                  </div>
                  {!steps[s.key] && (
                    <div className="mt-3">
                      {s.key === "phone" && (<div className="flex gap-2"><Input placeholder="+974 ..." value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="kyc-phone-input" /><Button size="sm" disabled={busy || !phone} onClick={() => doStep("phone", phone)}>Verify</Button></div>)}
                      {s.key === "id" && (<div><input type="file" accept="image/*" ref={idRef} className="hidden" onChange={() => upload("id", idRef)} data-testid="kyc-id-upload" /><Button size="sm" variant="secondary" disabled={busy} onClick={() => idRef.current?.click()}>Upload ID</Button></div>)}
                      {s.key === "selfie" && (<div><input type="file" accept="image/*" ref={selfieRef} className="hidden" onChange={() => upload("selfie", selfieRef)} data-testid="kyc-selfie-capture" /><Button size="sm" variant="secondary" disabled={busy} onClick={() => selfieRef.current?.click()}>Upload Selfie</Button></div>)}
                      {s.key === "email" && <div className="text-xs text-muted-foreground">Verified at signup</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {kyc?.status === "pending_review" && <p className="text-xs text-muted-foreground mt-3">All steps submitted. Your application is under review (admin approval).</p>}
          </Card>

          <Card className="p-5 shadow-premium" data-testid="contact-details-card">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-medium">Personal & contact details</div>
              <Badge variant="secondary" className="capitalize">{user?.role}</Badge>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Full name</Label>
                <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />Email</Label>
                <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />Phone</Label>
                <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="+974 ..." data-testid="contact-phone-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</Label>
                <Input value={contact.whatsapp} onChange={(e) => setContact({ ...contact, whatsapp: e.target.value })} placeholder="+974 ..." data-testid="contact-whatsapp-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Address</Label>
                <Input value={contact.address} onChange={(e) => setContact({ ...contact, address: e.target.value })} placeholder="Street, city, country" data-testid="contact-address-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" />Country of residence</Label>
                <Select value={contact.country} onValueChange={(v) => setContact({ ...contact, country: v, base_currency: COUNTRY_CCY[v] || contact.base_currency })}>
                  <SelectTrigger data-testid="contact-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Base currency</Label>
                <Select value={contact.base_currency} onValueChange={(v) => setContact({ ...contact, base_currency: v })}>
                  <SelectTrigger data-testid="contact-base-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{[...new Set(Object.values(COUNTRY_CCY))].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground">Changing your base currency opens a matching account in your Wallet.</p>
              <Button onClick={saveContact} disabled={contactBusy} className="gap-2 shrink-0" data-testid="contact-save-button">
                <SaveIcon className="h-4 w-4" />{contactBusy ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </Card>

          <Card className="p-5 shadow-premium" data-testid="ssl-security-card">
            <div className="flex items-center gap-2 text-sm font-medium mb-2"><Lock className="h-4 w-4 text-[color:var(--tharwy-lime-700)]" />SSL Encryption</div>
            <p className="text-xs text-muted-foreground leading-relaxed">Our website and app are secured with <span className="font-medium text-foreground">256-bit encryption</span> to keep your information safe and backed up continuously.</p>
          </Card>
        </div>

        <div className="space-y-4 sm:space-y-5">
          <ReferralCard />
          <ReferralLeaderboard />
          <WeeklyDigestCard />
          <MonthlyDigestCard />
          <Card className="p-5 shadow-premium">
            <div className="text-sm font-medium mb-3">Preferences</div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {theme === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                  <span className="text-sm">Appearance</span>
                </div>
                <div className="flex items-center gap-1 rounded-full bg-secondary p-1" data-testid="theme-switch">
                  {[["light", Sun, "Light"], ["dark", Moon, "Dark"]].map(([val, Icon, label]) => (
                    <button key={val} onClick={() => setTheme(val)}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                        theme === val ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                      data-testid={`theme-option-${val}`}>
                      <Icon className="h-3.5 w-3.5" />{label}
                    </button>
                  ))}
                </div>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Globe className="h-4 w-4" /><span className="text-sm">{t("language")}</span></div>
                <Button variant="secondary" size="sm" onClick={() => setLang(lang === "en" ? "ar" : "en")} data-testid="settings-language-toggle">{lang === "en" ? "English" : "العربية"}</Button>
              </div>
              <Separator />
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /><span className="text-sm">{t("pro_mode")}</span></div>
                  <p className="text-[11px] text-muted-foreground mt-1 max-w-[260px] leading-relaxed">
                    {t("pro_mode_hint")}
                  </p>
                </div>
                <Switch checked={!!user?.pro_mode} onCheckedChange={togglePro}
                  data-testid="settings-pro-mode-switch" />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Contrast className="h-4 w-4" /><span className="text-sm">{t("high_contrast")}</span></div>
                <Switch checked={contrast} onCheckedChange={setContrast} data-testid="settings-contrast-switch" />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /><span className="text-sm">Two-factor auth</span></div>
                <Switch data-testid="settings-2fa-toggle" />
              </div>
            </div>
          </Card>
          <HalalModeCard />
          <RiskProfileCard />
          <StatementsCard />
          <DeviceSessions />
          <PasskeyCard />
          <Card className="p-5 shadow-premium" data-testid="mfa-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium"><Lock className="h-4 w-4" />Two-Factor Authentication</div>
              <Badge variant={mfa.enabled ? "default" : "secondary"} data-testid="mfa-status-badge">{mfa.enabled ? "Enabled" : "Off"}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Protect your account with a time-based one-time password (TOTP) from an authenticator app such as Google Authenticator or Authy. You'll be asked for a code at sign-in.</p>

            {!mfa.enabled && !mfaSetup && (
              <Button onClick={startEnroll} disabled={mfaBusy} className="gap-2" data-testid="mfa-setup-button">
                <KeyRound className="h-4 w-4" />{mfaBusy ? "Please wait..." : "Set up 2FA"}
              </Button>
            )}

            {!mfa.enabled && mfaSetup && (
              <div className="space-y-3" data-testid="mfa-setup-panel">
                <div className="rounded-lg border p-3 bg-secondary">
                  <div className="text-xs text-muted-foreground mb-1">1. Add this secret key to your authenticator app</div>
                  <code className="text-sm font-semibold tracking-wider break-all" data-testid="mfa-secret">{mfaSetup.secret}</code>
                </div>
                <div className="space-y-1.5">
                  <Label>2. Enter the 6-digit code it generates</Label>
                  <Input inputMode="numeric" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456" className="tracking-[0.5em] text-center numeric" data-testid="mfa-verify-input" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={confirmEnable} disabled={mfaBusy} data-testid="mfa-verify-button">Verify &amp; enable</Button>
                  <Button variant="ghost" onClick={() => { setMfaSetup(null); setMfaCode(""); }} data-testid="mfa-cancel-button">Cancel</Button>
                </div>
              </div>
            )}

            {mfa.enabled && (
              <div className="space-y-3" data-testid="mfa-disable-panel">
                <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" />Your account is protected with 2FA.</div>
                <div className="space-y-1.5">
                  <Label>Enter a current code to disable 2FA</Label>
                  <Input inputMode="numeric" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456" className="tracking-[0.5em] text-center numeric" data-testid="mfa-disable-input" />
                </div>
                <Button variant="destructive" onClick={disableMfa} disabled={mfaBusy} data-testid="mfa-disable-button">Disable 2FA</Button>
              </div>
            )}
          </Card>

          <Card className="p-5 shadow-premium" data-testid="alert-preferences">
            <div className="flex items-center gap-2 text-sm font-medium mb-3"><Bell className="h-4 w-4" />Alert Preferences</div>
            <div className="space-y-3">
              {ALERT_OPTIONS.map((o, i) => (
                <React.Fragment key={o.key}>
                  {i > 0 && <Separator />}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <o.icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div><div className="text-sm font-medium">{o.label}</div><div className="text-xs text-muted-foreground">{o.desc}</div></div>
                    </div>
                    <Switch checked={!!alertPrefs[o.key]} onCheckedChange={(v) => toggleAlert(o.key, v)} data-testid={`alert-pref-${o.key}`} />
                  </div>
                </React.Fragment>
              ))}
            </div>
          </Card>
          <Card className="p-5 shadow-premium">
            <div className="flex items-center gap-2 text-sm font-medium mb-2"><ShieldCheck className="h-4 w-4 text-emerald-600" />Compliance</div>
            <p className="text-xs text-muted-foreground">Tharwy conducts AML screening and risk suitability checks. Islamic compliance disclosures apply to all Halal products. QFC regulatory disclosures shown for illustration.</p>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
